/**
 * DashboardStickyHeader.jsx — Phase 147 (Dashboard Cleanup), extracted from
 * Dashboard.jsx unchanged. The sticky tab switcher + Check in/out/Log time
 * (always visible, every tab) plus the Overview-only Ultra-Wide Command
 * Center row (Master Filter Bar + Metric Grid + Quick Add) — see Phase
 * 144/146. Nested `TabSection` uses the exact same activeTab/id as the
 * Task Hub list below so nothing unmounts on tab switch.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag } from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import TaskMasterFilterBar from "@/components/TaskMasterFilterBar";
import HomeTaskMetrics from "@/components/HomeTaskMetrics";
import CreateMenu from "@/components/CreateMenu";
import TabSection from "@/components/dashboard/TabSection";
import { TABS } from "@/lib/dashboardHelpers";
import { LinkedInPostGate } from "@/components/LinkedInPostGate";
import { LogIn, LogOut, Clock as ClockIcon, Heart, Image as ImageIcon, X } from "lucide-react";

export default function DashboardStickyHeader({
  activeTab, setActiveTab, canFinance, nav,
  checkedIn, checkedOut, eligibleCheckout, minutesToday,
  checkIn, checkOut, undoCheckOut,
  isFounder, gratitudeToday, submitGratitude,
  linkedinToday, reloadLinkedin,
  team, projects, currentUserId,
  taskPersonId, setTaskPersonId,
  taskProjectId, setTaskProjectId,
  taskBusinessUnit, setTaskBusinessUnit,
  taskStatusFilter, setTaskStatusFilter,
}) {
  const [gratitudeText, setGratitudeText] = useState("");
  const [gratitudeImageUrl, setGratitudeImageUrl] = useState("");
  const [gratitudeImageName, setGratitudeImageName] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  // Non-founders must log one gratitude note before Check-out unlocks.
  const gratitudeDone = isFounder || !!gratitudeToday;
  // On a LinkedIn rotation day, the assignee must submit their post first.
  const myLinkedinDay = !isFounder && linkedinToday?.is_working_day && linkedinToday?.is_me;
  const linkedinStatus = linkedinToday?.my_post?.status || null;
  const linkedinDone = !myLinkedinDay || ["pending", "approved", "posted"].includes(linkedinStatus);
  const canCheckout = eligibleCheckout && gratitudeDone && linkedinDone;

  const handleGratitudeFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Only JPG or PNG images are allowed."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be 5 MB or smaller."); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/gratitude-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGratitudeImageUrl(data.url);
      setGratitudeImageName(f.name);
    } catch { toast.error("Could not upload image"); }
    finally { setUploadingImage(false); }
  };

  const doSubmitGratitude = () => {
    if (!gratitudeText.trim()) return;
    submitGratitude(gratitudeText.trim(), gratitudeImageUrl || null);
    setGratitudeText(""); setGratitudeImageUrl(""); setGratitudeImageName("");
  };

  return (
    <div className="relative lg:sticky lg:top-0 z-20 py-3 mb-5 bg-[#F8F9FA]" data-testid="dashboard-tabs">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex gap-1">
          {TABS.filter((t) => t.id !== "finance" || canFinance).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`px-4 sm:px-5 py-2 text-sm font-medium rounded-sm transition-colors ${
                activeTab === t.id
                  ? "bg-[#F54800] text-white"
                  : "text-slate-600 hover:bg-white/70"
              }`}
              data-testid={`dashboard-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Phase 116 — compact Check in/out + Log time, always visible. */}
        <div className="flex items-center gap-2 shrink-0" data-testid="quick-actions">
          {!checkedIn && (
            <Button size="sm" onClick={checkIn} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="dash-checkin">
              <LogIn size={13} className="mr-1.5" /> Check in
            </Button>
          )}
          {checkedIn && !checkedOut && !isFounder && !gratitudeToday && (
            <div className="flex flex-col gap-1" data-testid="gratitude-inline">
              <div className="flex items-center gap-1.5">
                <Input
                  value={gratitudeText}
                  onChange={(e) => setGratitudeText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doSubmitGratitude(); }}
                  placeholder="What are you grateful for today?"
                  maxLength={1000}
                  className="rounded-sm h-9 text-xs w-52 sm:w-64"
                  data-testid="gratitude-input"
                />
                <Button
                  size="sm"
                  onClick={doSubmitGratitude}
                  disabled={!gratitudeText.trim() || uploadingImage}
                  className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:opacity-50"
                  data-testid="gratitude-submit"
                >
                  Submit
                </Button>
              </div>
              {gratitudeImageUrl ? (
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500" data-testid="gratitude-image-chosen">
                  <ImageIcon size={11} className="text-green-600" />
                  <span className="truncate max-w-[160px]">{gratitudeImageName}</span>
                  <button
                    type="button"
                    onClick={() => { setGratitudeImageUrl(""); setGratitudeImageName(""); }}
                    className="text-slate-400 hover:text-red-600"
                    title="Remove image"
                    data-testid="gratitude-image-remove"
                  >
                    <X size={11} />
                  </button>
                </div>
              ) : (
                <label className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 cursor-pointer" data-testid="gratitude-image-label">
                  <input type="file" accept="image/png,image/jpeg" hidden onChange={handleGratitudeFile} data-testid="gratitude-image-input" />
                  <ImageIcon size={11} /> {uploadingImage ? "Uploading…" : "Add a photo (JPG/PNG, optional)"}
                </label>
              )}
            </div>
          )}
          {checkedIn && !checkedOut && !isFounder && gratitudeToday && (
            <Tag tone="green" data-testid="gratitude-done">
              <Heart size={11} className="inline mr-1 -mt-0.5" />Grateful
            </Tag>
          )}
          {checkedIn && !checkedOut && myLinkedinDay && (
            <LinkedInPostGate data={linkedinToday} onChanged={reloadLinkedin} />
          )}
          {checkedIn && !checkedOut && (
            <Button
              size="sm"
              onClick={checkOut}
              disabled={!canCheckout}
              title={!gratitudeDone ? "Share one thing you're grateful for first" : (!linkedinDone ? "Submit your LinkedIn post for approval first" : (eligibleCheckout ? "" : `Log ${480 - minutesToday} more minutes first`))}
              className={`rounded-sm text-white ${canCheckout ? "bg-green-600 hover:bg-green-700" : "bg-slate-300 cursor-not-allowed"}`}
              data-testid="dash-checkout"
            >
              <LogOut size={13} className="mr-1.5" /> Check out
            </Button>
          )}
          {checkedIn && checkedOut && (
            <>
              <Tag tone="green" data-testid="dash-checkedout">Done ✓</Tag>
              <button
                type="button"
                onClick={undoCheckOut}
                className="text-[11px] text-red-600 hover:underline"
                data-testid="dash-undo-checkout"
                title="Clear this check-out timestamp"
              >
                Undo
              </button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => nav("/my-work?tab=timesheet")} className="rounded-sm" data-testid="dash-log-time">
            <ClockIcon size={13} className="mr-1.5" /> Log time
          </Button>
        </div>
      </div>

      {/* Phase 144/146 — Ultra-Wide Toolbar Header + Metric Grid + Quick Add,
          Overview-only, sticky as part of this same wrapper.
          Phase 149 — Bar Unification: Filters, Stats and Quick Actions now
          share ONE card (subtle top-border dividers between rows) instead
          of three stacked cards, to reclaim vertical space. */}
      <TabSection activeTab={activeTab} id="overview">
        <div className="mt-3 card-flat p-4" data-testid="home-command-center-wrap">
          <TaskMasterFilterBar
            team={team}
            projects={projects}
            currentUserId={currentUserId}
            personId={taskPersonId}
            onPersonChange={setTaskPersonId}
            projectId={taskProjectId}
            onProjectChange={setTaskProjectId}
            businessUnit={taskBusinessUnit}
            onBusinessUnitChange={setTaskBusinessUnit}
            statusFilter={taskStatusFilter}
            onStatusFilterChange={setTaskStatusFilter}
          />
          <div className="mt-4 pt-4 border-t border-slate-100">
            <HomeTaskMetrics
              statusFilter={taskStatusFilter}
              onStatusFilterChange={setTaskStatusFilter}
              personId={taskPersonId}
              projectId={taskProjectId}
              businessUnit={taskBusinessUnit}
            />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <CreateMenu variant="bar" />
          </div>
        </div>
      </TabSection>
    </div>
  );
}
